import React, { Component } from 'react';
import cn from 'classnames';
import isObject from 'lodash/isObject';
import isDefined from '../../utils/isDefined';
import * as valueTransformer from './helpers/valueTransformer';
import Label from './Label';
import Slider from './Slider';
import Track from './Track';
import { DOWN_ARROW, LEFT_ARROW, RIGHT_ARROW, UP_ARROW } from './helpers/keyCodes';
import { distanceTo, length } from './utils';

import { ValueType, PointType, PositionType } from './types';
import styles from './inputRange.module.scss';

interface IProps {
  /**
   * Allow the minimum and maximum values to be the same
   */
  allowSameValues?: boolean,
  /**
   * Defines the `aria-labelledby` attribute to improve accessibility
   */
  ariaLabelledby?: string,
  /**
   * Defines the `aria-controls` attribute to improve accessibility
   */
  ariaControls?: string,
  /**
   * Disables the input range if set to true
   */
  disabled?: boolean,
  /**
   * Allows the track to be draggable
   */
  draggableTrack?: boolean,
  /**
   * Function to format the label for the slider values
   */
  formatLabel?: (value?: number, type?: string) => string,
  /**
   * Maximum value for the range.
   */
  maxValue?: number,
  /**
   * Minimum value for the range
   */
  minValue?: number,
  /**
   * Name attribute for the input element
   */
  name?: string,
  /**
   * Callback function triggered when the user starts changing the value
   */
  onChangeStart?: (values?: ValueType) => void,
  /**
   * Callback function triggered when the value changes
   */
  onChange: (value?: number | ValueType) => void,
  /**
   * Callback function triggered when the user finishes changing the value
   */
  onChangeComplete?: (values?: ValueType) => void,
  /**
   * Step interval for the slider values
   */
  step?: number,
  /**
   * Current value of the slider
   */
  value?: ValueType,
  /**
   * Show label for the current value
   */
  showValueLabel?: boolean,
  /**
   * Show label for the minimum value
   */
  showMinLabel?: boolean,
  /**
   * Show label for the maximum value
   */
  showMaxLabel?: boolean,
}

const defaultProps: Partial<IProps> = {
  allowSameValues: true,
  disabled: false,
  maxValue: 10,
  minValue: 0,
  step: 1,
  showValueLabel: false,
  showMinLabel: false,
  showMaxLabel: false,
};

function capitalize(value) {
  if (typeof value !== 'string') {
    return value;
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
}

class InputRange extends Component<IProps> {
  static defaultProps: Partial<IProps> = defaultProps;

  // eslint-disable-next-line react/sort-comp
  private startValue: ValueType;

  private node: HTMLDivElement | null;

  private trackNode: any;

  private isSliderDragging: boolean;

  private lastKeyMoved: string;

  constructor(props: IProps) {
    super(props);

    this.startValue = null;

    this.node = null;

    this.trackNode = null;

    this.isSliderDragging = false;

    this.lastKeyMoved = null;

    this.handleSliderDrag = this.handleSliderDrag.bind(this);
    this.handleTrackDrag = this.handleTrackDrag.bind(this);
    this.handleSliderKeyDown = this.handleSliderKeyDown.bind(this);
    this.handleTrackMouseDown = this.handleTrackMouseDown.bind(this);
    this.handleInteractionStart = this.handleInteractionStart.bind(this);
    this.handleInteractionEnd = this.handleInteractionEnd.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
  }

  componentWillUnmount() {
    this.removeDocumentMouseUpListener();
    this.removeDocumentTouchEndListener();
  }

  /**
   * Return the CSS class name of the component
   */
  getComponentClassName() {
    if (!this.props.disabled) {
      return styles.inputRange;
    }

    return cn(styles.inputRange, styles.isDisabled);
  }

  /**
   * Return the bounding rect of the track
   */
  getTrackClientRect() {
    return this.trackNode.getClientRect();
  }

  /**
   * Return the slider key closest to a point
   */
  getKeyByPosition(position: PointType) {
    const values = valueTransformer.getValueFromProps(this.props, this.isMultiValue());
    const positions = valueTransformer.getPositionsFromValues(
      values,
      this.props.minValue,
      this.props.maxValue,
      this.getTrackClientRect(),
    );

    if (this.isMultiValue()) {
      const distanceToMin = distanceTo(position, positions.min);
      const distanceToMax = distanceTo(position, positions.max);

      if (distanceToMin < distanceToMax) {
        return 'min';
      }
    }

    return 'max';
  }

  /**
   * Return all the slider keys
   */
  getKeys() {
    if (this.isMultiValue()) {
      return ['min', 'max'];
    }

    return ['max'];
  }

  /**
   * Return true if the difference between the new and the current value is
   * greater or equal to the step amount of the component
   */
  hasStepDifference(values: ValueType) {
    const currentValues = valueTransformer.getValueFromProps(this.props, this.isMultiValue());

    return length(values.min, currentValues.min) >= this.props.step ||
           length(values.max, currentValues.max) >= this.props.step;
  }

  /**
   * Return true if the component accepts a min and max value
   */
  isMultiValue(): boolean {
    return isObject(this.props.value);
  }

  /**
   * Return true if the range is within the max and min value of the component
   */
  isWithinRange(values: ValueType): boolean {
    if (this.isMultiValue()) {
      return values.min >= this.props.minValue &&
             values.max <= this.props.maxValue &&
             this.props.allowSameValues
        ? values.min <= values.max
        : values.min < values.max;
    }

    return values.max >= this.props.minValue && values.max <= this.props.maxValue;
  }

  /**
   * Return true if the new value should trigger a render
   */
  shouldUpdate(values: ValueType): boolean {
    return this.isWithinRange(values) && this.hasStepDifference(values);
  }

  /**
   * Update the position of a slider
   */
  updatePosition(key: string, position: PointType) {
    const values = valueTransformer.getValueFromProps(this.props, this.isMultiValue());
    const positions = valueTransformer.getPositionsFromValues(
      values,
      this.props.minValue,
      this.props.maxValue,
      this.getTrackClientRect(),
    );

    positions[key] = position;
    this.lastKeyMoved = key;

    this.updatePositions(positions);
  }

  /**
   * Update the positions of multiple sliders
   */
  updatePositions(positions: PositionType) {
    const values = {
      min: valueTransformer.getValueFromPosition(
        positions.min,
        this.props.minValue,
        this.props.maxValue,
        this.getTrackClientRect(),
      ),
      max: valueTransformer.getValueFromPosition(
        positions.max, this.props.minValue,
        this.props.maxValue,
        this.getTrackClientRect(),
      ),
    };

    const transformedValues = {
      min: valueTransformer.getStepValueFromValue(values.min, this.props.step),
      max: valueTransformer.getStepValueFromValue(values.max, this.props.step),
    };

    this.updateValues(transformedValues);
  }

  /**
   * Update the value of a slider
   */
  updateValue(key: string, value: number) {
    const values = valueTransformer.getValueFromProps(this.props, this.isMultiValue());

    values[key] = value;

    this.updateValues(values);
  }

  /**
   * Update the values of multiple sliders
   */
  updateValues(values: ValueType) {
    if (!this.shouldUpdate(values)) {
      return;
    }

    this.props.onChange(this.isMultiValue() ? values : values.max);
  }

  /**
   * Increment the value of a slider by key name
   */
  incrementValue(key: string) {
    const values = valueTransformer.getValueFromProps(this.props, this.isMultiValue());
    const value = values[key] + this.props.step;

    this.updateValue(key, value);
  }

  /**
   * Decrement the value of a slider by key name
   */
  decrementValue(key: string) {
    const values = valueTransformer.getValueFromProps(this.props, this.isMultiValue());
    const value = values[key] - this.props.step;

    this.updateValue(key, value);
  }

  /**
   * Listen to mouseup event
   */
  addDocumentMouseUpListener() {
    this.removeDocumentMouseUpListener();
    this.node.ownerDocument.addEventListener('mouseup', this.handleMouseUp);
  }

  /**
   * Listen to touchend event
   */
  addDocumentTouchEndListener() {
    this.removeDocumentTouchEndListener();
    this.node.ownerDocument.addEventListener('touchend', this.handleTouchEnd);
  }

  /**
   * Stop listening to mouseup event
   */
  removeDocumentMouseUpListener() {
    this.node.ownerDocument.removeEventListener('mouseup', this.handleMouseUp);
  }

  /**
   * Stop listening to touchend event
   */
  removeDocumentTouchEndListener() {
    this.node.ownerDocument.removeEventListener('touchend', this.handleTouchEnd);
  }

  /**
   * Handle any "mousemove" event received by the slider
   */
  handleSliderDrag(event: MouseEvent | TouchEvent, key: string) {
    if (this.props.disabled) {
      return;
    }

    const position = valueTransformer.getPositionFromEvent(event, this.getTrackClientRect());
    this.isSliderDragging = true;
    requestAnimationFrame(() => this.updatePosition(key, position));
  }

  /**
   * Handle any "mousemove" event received by the track
   */
  handleTrackDrag(event: React.SyntheticEvent, prevEvent: React.SyntheticEvent) {
    if (this.props.disabled || !this.props.draggableTrack || this.isSliderDragging) {
      return;
    }

    const {
      maxValue,
      minValue,
      value: { max, min },
    } = this.props;

    const position = valueTransformer.getPositionFromEvent(event, this.getTrackClientRect());
    const value = valueTransformer.getValueFromPosition(position, minValue, maxValue, this.getTrackClientRect());
    const stepValue = valueTransformer.getStepValueFromValue(value, this.props.step);

    const prevPosition = valueTransformer.getPositionFromEvent(prevEvent, this.getTrackClientRect());
    const prevValue = valueTransformer.getValueFromPosition(
      prevPosition,
      minValue,
      maxValue,
      this.getTrackClientRect(),
    );
    const prevStepValue = valueTransformer.getStepValueFromValue(prevValue, this.props.step);

    const offset = prevStepValue - stepValue;

    const transformedValues = {
      min: min - offset,
      max: max - offset,
    };

    this.updateValues(transformedValues);
  }

  /**
   * Handle any "keydown" event received by the slider
   */
  handleSliderKeyDown(event: React.KeyboardEvent, key: string) {
    if (this.props.disabled) {
      return;
    }

    switch (event.keyCode) {
    case LEFT_ARROW:
    case DOWN_ARROW:
      event.preventDefault();
      this.decrementValue(key);
      break;

    case RIGHT_ARROW:
    case UP_ARROW:
      event.preventDefault();
      this.incrementValue(key);
      break;

    default:
      break;
    }
  }

  /**
   * Handle any "mousedown" event received by the track
   */
  handleTrackMouseDown(event: React.SyntheticEvent, position: PointType) {
    if (this.props.disabled) {
      return;
    }

    const {
      maxValue,
      minValue,
      value: { max, min },
    } = this.props;

    event.preventDefault();

    const value = valueTransformer.getValueFromPosition(position, minValue, maxValue, this.getTrackClientRect());
    const stepValue = valueTransformer.getStepValueFromValue(value, this.props.step);

    if (!this.props.draggableTrack || stepValue > max || stepValue < min) {
      this.updatePosition(this.getKeyByPosition(position), position);
    }
  }

  /**
   * Handle the start of any mouse/touch event
   */
  handleInteractionStart() {
    if (this.props.onChangeStart) {
      this.props.onChangeStart(this.props.value);
    }

    if (this.props.onChangeComplete && !isDefined(this.startValue)) {
      this.startValue = this.props.value;
    }
  }

  /**
   * Handle the end of any mouse/touch event
   */
  handleInteractionEnd() {
    if (this.isSliderDragging) {
      this.isSliderDragging = false;
    }

    if (!this.props.onChangeComplete || !isDefined(this.startValue)) {
      return;
    }

    if (this.startValue !== this.props.value) {
      this.props.onChangeComplete(this.props.value);
    }

    this.startValue = null;
  }

  /**
   * Handle any "keydown" event received by the component
   */
  handleKeyDown() {
    this.handleInteractionStart();
  }

  /**
   * Handle any "keyup" event received by the component
   */
  handleKeyUp() {
    this.handleInteractionEnd();
  }

  /**
   * Handle any "mousedown" event received by the component
   */
  handleMouseDown() {
    this.handleInteractionStart();
    this.addDocumentMouseUpListener();
  }

  /**
   * Handle any "mouseup" event received by the component
   */
  handleMouseUp() {
    this.handleInteractionEnd();
    this.removeDocumentMouseUpListener();
  }

  /**
   * Handle any "touchstart" event received by the component
   */
  handleTouchStart() {
    this.handleInteractionStart();
    this.addDocumentTouchEndListener();
  }

  /**
   * Handle any "touchend" event received by the component
   */
  handleTouchEnd() {
    this.handleInteractionEnd();
    this.removeDocumentTouchEndListener();
  }

  /**
   * Return JSX of sliders
   */
  renderSliders(): React.ReactNode {
    const values = valueTransformer.getValueFromProps(this.props, this.isMultiValue());
    const percentages = valueTransformer.getPercentagesFromValues(values, this.props.minValue, this.props.maxValue);
    const keys = this.props.allowSameValues &&
                 this.lastKeyMoved === 'min'
      ? this.getKeys().reverse()
      : this.getKeys();

    return keys.map((key) => {
      let value = values[key];
      let percentage = percentages[key];

      let { maxValue, minValue } = this.props;

      if (key === 'min') {
        maxValue = values.max;
      } else {
        minValue = values.min;
      }

      // Disabled
      if (maxValue === 0 && key === 'max') {
        percentage = 1;
        value = 1;
      }

      return (
        <Slider
          ariaLabelledby={ this.props.ariaLabelledby }
          ariaControls={ this.props.ariaControls }
          formatLabel={ this.props.formatLabel }
          key={ key }
          maxValue={ maxValue }
          minValue={ minValue }
          onSliderDrag={ this.handleSliderDrag }
          onSliderKeyDown={ this.handleSliderKeyDown }
          percentage={ percentage }
          type={ key }
          value={ value }
          showValueLabel={ this.props.showValueLabel }
          disabled={ this.props.disabled }
        />
      );
    });
  }

  /**
   * Return JSX of hidden inputs
   */
  renderHiddenInputs(): React.ReactNode {
    if (!this.props.name) {
      return [];
    }

    const isMultiValue = this.isMultiValue();
    const values = valueTransformer.getValueFromProps(this.props, isMultiValue);

    return this.getKeys().map((key) => {
      const value = values[key];
      const name = isMultiValue ? `${ this.props.name }${ capitalize(key) }` : this.props.name;

      return (
        <input key={ key } type="hidden" name={ name } value={ value } />
      );
    });
  }

  render(): React.ReactElement {
    const componentClassName = this.getComponentClassName();
    const values = valueTransformer.getValueFromProps(this.props, this.isMultiValue());
    let percentages = valueTransformer.getPercentagesFromValues(values, this.props.minValue, this.props.maxValue);

    // Disabled
    if (this.props.maxValue === 0) {
      percentages.max = 1;
    }

    return (
      <div
        aria-disabled={ this.props.disabled }
        ref={ (node) => { this.node = node; } }
        className={ componentClassName }
        onKeyDown={ this.handleKeyDown }
        onKeyUp={ this.handleKeyUp }
        onMouseDown={ this.handleMouseDown }
        onTouchStart={ this.handleTouchStart }
      >
        { this.props.showMinLabel && (
          <Label
            formatLabel={ this.props.formatLabel }
            type="min"
          >
            { this.props.minValue }
          </Label>
        ) }

        <Track
          draggableTrack={ this.props.draggableTrack }
          ref={ (trackNode) => { this.trackNode = trackNode; } }
          percentages={ percentages }
          onTrackDrag={ this.handleTrackDrag }
          onTrackMouseDown={ this.handleTrackMouseDown }
          disabled={ this.props.disabled }
        >
          { this.renderSliders() }
        </Track>

        { this.props.showMaxLabel && (
          <Label
            formatLabel={ this.props.formatLabel }
            type="max"
          >
            { this.props.maxValue }
          </Label>
        ) }

        { this.renderHiddenInputs() }
      </div>
    );
  }
}

export default InputRange;
